// Evaluation calendar (PRD-61 FR-1) — the draft model the console edits, and
// the rules the API enforces, run on the client so a mistake is shown next
// to the date that caused it instead of round-tripping to a 422. Pure: no
// React, no network. The UI lives in `@components/evaluation-calendar/*`.
//
// The API validates the same things and is canonical (PRD-61 FR-1.3): if the
// two ever disagree, the 422 code is shown as a form-level message.

import { addDays, format, isValid, parseISO } from "date-fns"

import {
  CALENDAR_QUICK_START,
  DEFAULT_CUTS_PER_PERIOD,
  DEFAULT_CUT_NOUN,
  MAX_CUTS_PER_PERIOD,
  MIN_CUTS_PER_PERIOD,
  type CutNoun,
  type PeriodType,
} from "@/constants/academic"
import type { EvaluationPeriodIn } from "@/types"

/** A period while the admin edits it. Empty dates are "". */
export interface PeriodDraft {
  /** Client-only React key; never sent. */
  key: string
  label: string
  start_date: string
  end_date: string
  noun: CutNoun
  /** Closing date of every cut but the last — its length is the cut count − 1. */
  cut_dates: string[]
}

let sequence = 0
const nextKey = () => `period-${++sequence}`

export const cutCount = (draft: PeriodDraft) => draft.cut_dates.length + 1

const clampCuts = (n: number) => Math.min(MAX_CUTS_PER_PERIOD, Math.max(MIN_CUTS_PER_PERIOD, n))

export function newPeriodDraft(
  noun: CutNoun = DEFAULT_CUT_NOUN,
  cuts: number = DEFAULT_CUTS_PER_PERIOD,
): PeriodDraft {
  return {
    key: nextKey(),
    label: "",
    start_date: "",
    end_date: "",
    noun,
    cut_dates: Array.from({ length: clampCuts(cuts) - 1 }, () => ""),
  }
}

export function toDrafts(periods: EvaluationPeriodIn[]): PeriodDraft[] {
  return periods.map((p) => ({
    key: nextKey(),
    label: p.label,
    start_date: p.start_date,
    end_date: p.end_date,
    noun: p.noun,
    cut_dates: [...p.cut_dates],
  }))
}

/** The shape a school with this period type usually has — never its dates. */
export function quickStartDrafts(type: PeriodType): PeriodDraft[] {
  const shape = CALENDAR_QUICK_START[type]
  return Array.from({ length: shape.periods }, () => newPeriodDraft(shape.noun, shape.cuts))
}

/** Grow or shrink the cuts, keeping every closing date already typed. */
export function withCutCount(draft: PeriodDraft, count: number): PeriodDraft {
  const n = clampCuts(count) - 1
  const cut_dates = draft.cut_dates.slice(0, n)
  while (cut_dates.length < n) cut_dates.push("")
  return { ...draft, cut_dates }
}

function normalize(periods: EvaluationPeriodIn[]): EvaluationPeriodIn[] {
  return periods.map((p) => ({
    label: p.label.trim(),
    start_date: p.start_date,
    end_date: p.end_date,
    noun: p.noun,
    cut_dates: [...p.cut_dates],
  }))
}

/** The PUT body. */
export function toPayload(drafts: PeriodDraft[]): EvaluationPeriodIn[] {
  return normalize(drafts)
}

/** What a set of periods says, ignoring React keys and the server's `cuts`. */
export function calendarSignature(periods: EvaluationPeriodIn[]): string {
  return JSON.stringify(normalize(periods))
}

// ── Dates ──────────────────────────────────────────────────────────────────

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/
// A date input emits "0002-…", "0020-…", "0202-…" while the year is typed
// digit by digit; those are not dates anybody meant, so the live pass must
// not judge them.
const FIRST_PLAUSIBLE_YEAR = 1900

/** The value, when it is a real calendar day; null for "" or garbage. ISO
 *  days compare correctly as strings, so the rules below never build Dates. */
function day(value: string): string | null {
  return ISO_DAY.test(value) &&
    Number(value.slice(0, 4)) >= FIRST_PLAUSIBLE_YEAR &&
    isValid(parseISO(value))
    ? value
    : null
}

/** The widest span a period of "2025-2026" can plausibly use — for the date
 *  pickers only. The API does not bound periods by the school year, so
 *  neither does validation. */
export function schoolYearBounds(schoolYear: string): { min: string; max: string } | null {
  const match = /^(\d{4})-(\d{4})$/.exec(schoolYear)
  return match ? { min: `${match[1]}-01-01`, max: `${match[2]}-12-31` } : null
}

function dayAfter(value: string): string | null {
  const d = day(value)
  return d ? format(addDays(parseISO(d), 1), "yyyy-MM-dd") : null
}

export interface CutPreview {
  number: number
  start: string | null
  end: string | null
}

/**
 * Each cut's span as the professor will see it: the first starts with the
 * period, each next one the day after the previous closes (the closing day
 * is inclusive, PRD-61 FR-2.1), the last closes with the period.
 */
export function previewCuts(draft: PeriodDraft): CutPreview[] {
  const n = cutCount(draft)
  return Array.from({ length: n }, (_, i) => {
    const previousClose = draft.cut_dates[i - 1]
    return {
      number: i + 1,
      start: i === 0 ? day(draft.start_date) : previousClose ? dayAfter(previousClose) : null,
      end: i === n - 1 ? day(draft.end_date) : day(draft.cut_dates[i] ?? ""),
    }
  })
}

// ── Validation ─────────────────────────────────────────────────────────────

/** `cut_1` is the closing date of the first cut. */
export type PeriodField = "label" | "start_date" | "end_date" | `cut_${number}`

export const cutField = (index: number): PeriodField => `cut_${index + 1}`

/**
 * Client-only codes, plus the API's own 422 codes where the rule is the
 * same one — so a field says exactly what the server would have said.
 */
export type CalendarErrorCode =
  | "label_required"
  | "date_required"
  | "period_dates_invalid"
  | "cut_dates_invalid"
  | "periods_overlap"

export interface FieldError {
  code: CalendarErrorCode
  /** `periods_overlap`: the 1-based number of the period it runs into. */
  other?: number
}

export type CalendarErrors = Record<string, Partial<Record<PeriodField, FieldError>>>

/**
 * `complete: false` is the live pass while the admin types: it reports only
 * contradictions between dates already written, never a field left empty.
 * `complete: true` is the pass on Save.
 */
export function validateCalendar(
  drafts: PeriodDraft[],
  { complete }: { complete: boolean },
): CalendarErrors {
  const errors: CalendarErrors = {}
  const set = (key: string, field: PeriodField, error: FieldError) => {
    const bucket = (errors[key] ??= {})
    bucket[field] ??= error
  }

  drafts.forEach((d) => {
    if (complete && !d.label.trim()) set(d.key, "label", { code: "label_required" })
    const start = day(d.start_date)
    const end = day(d.end_date)
    if (complete && !start) set(d.key, "start_date", { code: "date_required" })
    if (complete && !end) set(d.key, "end_date", { code: "date_required" })
    if (start && end && start > end) set(d.key, "end_date", { code: "period_dates_invalid" })

    // Strictly increasing, and strictly inside (start, end).
    let previous = start
    d.cut_dates.forEach((raw, i) => {
      const close = day(raw)
      if (!close) {
        if (complete) set(d.key, cutField(i), { code: "date_required" })
        return
      }
      if ((previous && close <= previous) || (end && close >= end)) {
        set(d.key, cutField(i), { code: "cut_dates_invalid" })
      }
      previous = close
    })
  })

  // Two periods of one school year cannot share a day.
  const spans = drafts
    .map((d, index) => ({ d, index, start: day(d.start_date), end: day(d.end_date) }))
    .filter((s): s is typeof s & { start: string; end: string } => !!s.start && !!s.end)
    .filter((s) => s.start <= s.end)
    .sort((a, b) => a.start.localeCompare(b.start))
  spans.forEach((later, i) => {
    const earlier = spans.slice(0, i).find((s) => later.start <= s.end)
    if (earlier)
      set(later.d.key, "start_date", { code: "periods_overlap", other: earlier.index + 1 })
  })

  return errors
}

const FIELD_ORDER = (d: PeriodDraft): PeriodField[] => [
  "label",
  "start_date",
  "end_date",
  ...d.cut_dates.map((_, i) => cutField(i)),
]

/** The first invalid field in reading order — where focus goes after Save. */
export function firstInvalidField(
  drafts: PeriodDraft[],
  errors: CalendarErrors,
  code?: CalendarErrorCode,
): { key: string; field: PeriodField } | null {
  for (const d of drafts) {
    const bucket = errors[d.key]
    if (!bucket) continue
    const field = FIELD_ORDER(d).find((f) => bucket[f] && (!code || bucket[f]?.code === code))
    if (field) return { key: d.key, field }
  }
  return null
}

/** The API's 422 codes for this surface (contract §Error codes). */
export const CALENDAR_API_ERRORS = [
  "period_dates_invalid",
  "cut_dates_invalid",
  "too_many_cuts",
  "periods_overlap",
  "school_year_invalid",
] as const

export type CalendarApiError = (typeof CALENDAR_API_ERRORS)[number]

export function isCalendarApiError(code: string | null): code is CalendarApiError {
  return !!code && (CALENDAR_API_ERRORS as readonly string[]).includes(code)
}

/** A stable DOM handle for a field, so focus can reach it by key. */
export const fieldHandle = (key: string, field: PeriodField) => `${key}:${field}`
